/* ============================================================
   Native Possum — np-header.js
   Mobile search + predictive search (client-side filtering), mobile and
   tablet header scroll behaviour, and --mobile-header-height on :root
   (ResizeObserver on .mobile-header-wrapper).
   Desktop search, sticky nav, currency, dropdowns and bag drawer live in
   header-scripts.liquid.
   Depends on: np-utilities.js (npEscapeHtml, npFormatMoney).
   ============================================================ */

(function() {
  'use strict';

  // ============================================
  // MOBILE HEADER HEIGHT — set CSS variable
  // ============================================
  var headerWrapper = document.querySelector('.mobile-header-wrapper');
  if (headerWrapper) {
    var setHeaderHeight = function() {
      document.documentElement.style.setProperty(
        '--mobile-header-height',
        headerWrapper.offsetHeight + 'px'
      );
    };
    setHeaderHeight();
    var ro = new ResizeObserver(setHeaderHeight);
    ro.observe(headerWrapper);
  }

  // v15: Lock viewport height globally (prevents iOS address bar layout shift)
  // Previously only set in np-product-mobile.js for product pages
  document.documentElement.style.setProperty('--np-vh', window.innerHeight + 'px');

  // ============================================
  // MOBILE ELEMENTS
  // ============================================
  
  var mobileSearchToggle = document.getElementById('mobileSearchToggle');
  var mobileSearchExpanded = document.getElementById('mobileSearchExpanded');
  var mobileSearchClose = document.getElementById('mobileSearchClose');
  var mobileSearchInput = document.getElementById('mobileSearchInput');
  var mobilePredictiveSearch = document.getElementById('mobilePredictiveSearch');
  var mobileHeaderWrapper = document.getElementById('mobileHeaderWrapper');
  var tabletHeaderWrapper = document.getElementById('tabletHeaderWrapper');
  // State
  var isMobileSearchOpen = false;
  var searchDebounceTimer = null;

  // ============================================
  // UTILITY FUNCTIONS
  // ============================================
  
  var escapeHtml = window.npEscapeHtml;
  var formatMoney = window.npFormatMoney;

  // ============================================
  // MOBILE SEARCH
  // ============================================
  
  function openMobileSearch() {
    if (!mobileSearchExpanded) return;
    isMobileSearchOpen = true;
    mobileSearchExpanded.classList.add('is-open');
    mobileSearchExpanded.setAttribute('aria-hidden', 'false');
    if (mobileSearchToggle) mobileSearchToggle.setAttribute('aria-expanded', 'true');
    if (mobileHeaderWrapper) {
      mobileHeaderWrapper.classList.add('search-open');
      mobileHeaderWrapper.style.zIndex = '10004';
    }
    if (mobileSearchInput) mobileSearchInput.focus();
  }
  
  function closeMobileSearch() {
    if (!mobileSearchExpanded) return;
    isMobileSearchOpen = false;
    mobileSearchExpanded.classList.remove('is-open');
    mobileSearchExpanded.setAttribute('aria-hidden', 'true');
    if (mobileSearchToggle) mobileSearchToggle.setAttribute('aria-expanded', 'false');
    if (mobileHeaderWrapper) {
      mobileHeaderWrapper.classList.remove('search-open');
      mobileHeaderWrapper.style.zIndex = '';
    }
    if (mobileSearchInput) mobileSearchInput.value = '';
    if (mobilePredictiveSearch) {
      mobilePredictiveSearch.classList.remove('active');
      mobilePredictiveSearch.innerHTML = '';
    }
  }
  
  function handleMobileSearchInput() {
    var query = mobileSearchInput.value.trim();
    clearTimeout(searchDebounceTimer);
    
    if (query.length < 2) {
      if (mobilePredictiveSearch) {
        mobilePredictiveSearch.classList.remove('active');
        mobilePredictiveSearch.innerHTML = '';
      }
      return;
    }
    
    searchDebounceTimer = setTimeout(function() {
      fetchMobilePredictiveResults(query);
    }, 300);
  }
  
  function fetchMobilePredictiveResults(query) {
    if (!mobilePredictiveSearch) return;
    
    mobilePredictiveSearch.innerHTML = '<div class="mobile-predictive-loading">Searching...</div>';
    mobilePredictiveSearch.classList.add('active');
    
    var searchUrl = window.Shopify && window.Shopify.routes && window.Shopify.routes.root 
      ? window.Shopify.routes.root + 'search/suggest.json'
      : '/search/suggest.json';
    
    var productUrl = searchUrl + '?q=' + encodeURIComponent(query) + '&resources[type]=product&resources[limit]=24&resources[options][fields]=title,product_type,vendor,tag,variants.sku';
    var contentUrl = searchUrl + '?q=' + encodeURIComponent(query) + '&resources[type]=article,page&resources[limit]=4';
    
    Promise.all([
      fetch(productUrl).then(function(r) { if (!r.ok) throw new Error('Search failed'); return r.json(); }),
      fetch(contentUrl).then(function(r) { if (!r.ok) throw new Error('Search failed'); return r.json(); })
    ])
      .then(function(results) {
        var combined = {
          resources: {
            results: {
              products: results[0].resources && results[0].resources.results ? results[0].resources.results.products || [] : [],
              articles: results[1].resources && results[1].resources.results ? results[1].resources.results.articles || [] : [],
              pages: results[1].resources && results[1].resources.results ? results[1].resources.results.pages || [] : []
            }
          }
        };
        renderMobilePredictiveResults(combined, query);
      })
      .catch(function(error) {
        console.error('Mobile search error:', error);
        mobilePredictiveSearch.innerHTML = '<div class="mobile-predictive-empty">Unable to load results</div>';
      });
  }
  
  function renderMobilePredictiveResults(data, query) {
    var results = data.resources && data.resources.results ? data.resources.results : {};
    var products = results.products || [];
    var articles = results.articles || [];
    var pages = results.pages || [];
    
    // Limit: 4 products, 2 articles/pages combined
    products = products.slice(0, 4);
    var contentResults = articles.concat(pages).slice(0, 2);
    
    var totalResults = products.length + contentResults.length;
    
    if (totalResults === 0) {
      renderMobileEmptyState(query);
      return;
    }
    
    var html = '<ul class="mobile-predictive-results">';
    
    // Render products first
    products.forEach(function(product) {
      var image = product.image || product.featured_image || '';
      
      html += '<li class="mobile-predictive-item">';
      html += '<a href="' + product.url + '" class="mobile-predictive-link">';
      if (image) {
        html += '<img src="' + image + '" alt="" class="mobile-predictive-image" loading="lazy">';
      }
      html += '<div class="mobile-predictive-content">';
      html += '<p class="mobile-predictive-title">' + escapeHtml(product.title) + '</p>';
      html += '</div>';
      html += '</a>';
      html += '</li>';
    });
    
    // Render articles and pages
    contentResults.forEach(function(item) {
      var isArticle = item.hasOwnProperty('published_at') || (item.url && item.url.indexOf('/blogs/') !== -1);
      var badgeText = isArticle ? 'Blog' : 'Page';
      
      html += '<li class="mobile-predictive-item mobile-predictive-item--content">';
      html += '<a href="' + item.url + '" class="mobile-predictive-link mobile-predictive-link--content">';
      html += '<div class="mobile-predictive-content mobile-predictive-content--full">';
      html += '<span class="mobile-predictive-badge">' + badgeText + '</span>';
      html += '<p class="mobile-predictive-title">' + escapeHtml(item.title) + '</p>';
      html += '</div>';
      html += '</a>';
      html += '</li>';
    });
    
    html += '</ul>';
    html += '<a href="/search?q=' + encodeURIComponent(query) + '" class="mobile-predictive-view-all">View all results</a>';
    
    mobilePredictiveSearch.innerHTML = html;
  }
  
  function renderMobileEmptyState(query) {
    var html = '<div class="mobile-predictive-empty-state">';
    
    // Icon
    html += '<div class="mobile-predictive-empty-icon" aria-hidden="true">';
    html += '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 -960 960 960">';
    html += '<path d="M784-120 532-372q-30 24-69 38t-83 14q-109 0-184.5-75.5T120-580q0-109 75.5-184.5T380-840q109 0 184.5 75.5T640-580q0 44-14 83t-38 69l252 252-56 56ZM380-400q75 0 127.5-52.5T560-580q0-75-52.5-127.5T380-760q-75 0-127.5 52.5T200-580q0 75 52.5 127.5T380-400Z"/>';
    html += '</svg>';
    html += '</div>';
    
    // Title
    html += '<h2 class="mobile-predictive-empty-title">No results for "' + escapeHtml(query) + '"</h2>';
    
    // Text
    html += '<p class="mobile-predictive-empty-text">Try a different search, or explore our collections below.</p>';
    
    // Category pills - full width stacked
    html += '<div class="mobile-predictive-empty-buttons">';
    html += '<a href="/collections/women" class="mobile-predictive-empty-btn">Women</a>';
    html += '<a href="/collections/men" class="mobile-predictive-empty-btn">Men</a>';
    html += '<a href="/collections/accessories" class="mobile-predictive-empty-btn">Accessories</a>';
    html += '<a href="/collections/blankets-throws" class="mobile-predictive-empty-btn">Blankets + Throws</a>';
    html += '</div>';
    
    // Contact link
    html += '<p class="mobile-predictive-empty-contact">Need help finding something? <a href="/pages/contact">Get in touch</a></p>';
    
    html += '</div>';
    
    mobilePredictiveSearch.innerHTML = html;
  }
  
  // Mobile search event listeners
  if (mobileSearchToggle) mobileSearchToggle.addEventListener('click', openMobileSearch);
  if (mobileSearchClose) mobileSearchClose.addEventListener('click', closeMobileSearch);
  if (mobileSearchInput) mobileSearchInput.addEventListener('input', handleMobileSearchInput);
  
  // Dismiss keyboard when scrolling search results
  if (mobilePredictiveSearch) {
    mobilePredictiveSearch.addEventListener('scroll', function() {
      if (document.activeElement && document.activeElement.blur) {
        document.activeElement.blur();
      }
    }, { passive: true });
  }
  
  // Escape key closes mobile search
  document.addEventListener('keydown', function(e) {
    if (e.key === 'Escape' && isMobileSearchOpen) {
      closeMobileSearch();
    }
  });

  // ============================================
  // MOBILE HEADER SCROLL
  // ============================================
  
  var ticking = false;
  
  function handleMobileScroll() {
    var scrollY = window.scrollY;
    
    if (mobileHeaderWrapper) {
      if (scrollY > 50) {
        mobileHeaderWrapper.classList.add('is-scrolled');
      } else {
        mobileHeaderWrapper.classList.remove('is-scrolled');
      }
    }
    
    if (tabletHeaderWrapper) {
      if (scrollY > 50) {
        tabletHeaderWrapper.classList.add('is-scrolled');
      } else {
        tabletHeaderWrapper.classList.remove('is-scrolled');
      }
    }
    
    ticking = false;
  }
  
  window.addEventListener('scroll', function() {
    if (!ticking) {
      window.requestAnimationFrame(handleMobileScroll);
      ticking = true;
    }
  });
  
  // Initial scroll check
  handleMobileScroll();

  // ============================================
  // POPULATE MOBILE SEARCH FROM URL (search results page)
  // ============================================
  if (mobileSearchInput && window.location.pathname === '/search') {
    var params = new URLSearchParams(window.location.search);
    var urlQuery = params.get('q');
    if (urlQuery) {
      mobileSearchInput.value = urlQuery;
    }
  }

  // ============================================
  // OFFLINE / ONLINE BANNER
  // ============================================
  var offlineBanner = document.createElement('div');
  offlineBanner.className = 'np-offline-banner';
  offlineBanner.setAttribute('role', 'alert');
  offlineBanner.setAttribute('aria-live', 'assertive');
  offlineBanner.textContent = 'No internet connection';
  document.body.appendChild(offlineBanner);

  function showOfflineBanner() {
    offlineBanner.classList.add('active');
  }

  function hideOfflineBanner() {
    offlineBanner.classList.remove('active');
  }

  window.addEventListener('offline', showOfflineBanner);
  window.addEventListener('online', hideOfflineBanner);

  if (!navigator.onLine) {
    showOfflineBanner();
  }

})();